import type { Presupuesto, EmpresaConfig, Cliente, ProductoServicio } from './types';
import { DEFAULT_LOGO_URL } from './defaultLogo';
import { today, addDays } from './calc';
import { sanitizeObject, validateBackupData } from './security';

const KEYS = {
  presupuestos: 'pp_presupuestos',
  config: 'pp_config',
  counter: 'pp_counter',
  clientes: 'pp_clientes',
  productos: 'pp_productos',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function read<T>(key: string): T[] {
  try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? []; } catch { return []; }
}
function write<T>(key: string, data: T[]): void {
  localStorage.setItem(key, JSON.stringify(data));
}

// ── Numeración ────────────────────────────────────────────────────────────────
function nextNumero(): string {
  const year = new Date().getFullYear();
  const n = parseInt(localStorage.getItem(KEYS.counter) ?? '0') + 1;
  localStorage.setItem(KEYS.counter, String(n));
  return `PRE-${year}-${String(n).padStart(3, '0')}`;
}

// ── Presupuestos ──────────────────────────────────────────────────────────────
export function getPresupuestos(): Presupuesto[] { return read<Presupuesto>(KEYS.presupuestos); }
function savePresupuestos(d: Presupuesto[]): void { write(KEYS.presupuestos, d); }

export function crearPresupuesto(): Presupuesto {
  const p: Presupuesto = {
    id: crypto.randomUUID(), numero: nextNumero(),
    fecha: today(), fechaValidez: addDays(today(), 30), estado: 'Borrador',
    clienteId: '', clienteNombre: '', clienteNIF: '', clienteDireccion: '', clienteEmail: '', clienteTelefono: '',
    aseguradora: '', numeroSiniestro: '', matricula: '',
    lineas: [], notas: '',
    condiciones: 'Presupuesto válido durante 30 días desde su emisión. Los precios incluyen mano de obra y materiales salvo indicación contraria.',
  };
  savePresupuestos([p, ...getPresupuestos()]);
  return p;
}
export function guardarPresupuesto(p: Presupuesto): void {
  savePresupuestos(getPresupuestos().map(x => (x.id === p.id ? sanitizeObject(p) : x)));
}
export function eliminarPresupuesto(id: string): void {
  savePresupuestos(getPresupuestos().filter(x => x.id !== id));
}
export function duplicarPresupuesto(id: string): Presupuesto {
  const orig = getPresupuestos().find(x => x.id === id);
  if (!orig) throw new Error('not found');
  const copy: Presupuesto = {
    ...orig, id: crypto.randomUUID(), numero: nextNumero(),
    fecha: today(), fechaValidez: addDays(today(), 30), estado: 'Borrador',
    lineas: orig.lineas.map(l => ({ ...l, id: crypto.randomUUID() })),
  };
  savePresupuestos([copy, ...getPresupuestos()]);
  return copy;
}

// ── Configuración ─────────────────────────────────────────────────────────────
// Datos correctos de la empresa — Oscar Alaminos Serrano
const DEFAULT_CONFIG: EmpresaConfig = {
  nombre:        'Reparaciones y Reformas Oscar Alaminos',
  nif:           '74739946C',
  direccion:     'Calle Jardines, 20',
  cp:            '18720',
  ciudad:        'Torrenueva Costa',
  telefono:      '623 625 922',
  email:         '',
  web:           'reformasoscaralaminos.es',
  iban:          '',
  logoURL:       DEFAULT_LOGO_URL,
  colorPrimario: '#2E5F5E',
  piePagina:     'Gracias por confiar en Reparaciones y Reformas Oscar Alaminos.',
};

// Clave de versión del seed de configuración
const CONFIG_SEED_V_KEY = 'pp_config_seed_v';
const CONFIG_SEED_V     = '4'; // v4 — añadir web reformasoscaralaminos.es

/** Aplica/corrige los datos de empresa en localStorage si el seed no está actualizado. */
export function ensureConfigSeed(): void {
  if (localStorage.getItem(CONFIG_SEED_V_KEY) === CONFIG_SEED_V) return;
  const stored = (() => {
    try { return JSON.parse(localStorage.getItem(KEYS.config) ?? '{}') as Partial<EmpresaConfig>; }
    catch { return {}; }
  })();
  // Sobrescribir siempre los datos de empresa con los valores correctos
  stored.nombre    = DEFAULT_CONFIG.nombre;
  stored.nif       = DEFAULT_CONFIG.nif;
  stored.direccion = DEFAULT_CONFIG.direccion;
  stored.cp        = DEFAULT_CONFIG.cp;
  stored.ciudad    = DEFAULT_CONFIG.ciudad;
  stored.telefono  = DEFAULT_CONFIG.telefono;
  stored.web       = DEFAULT_CONFIG.web;
  stored.piePagina = DEFAULT_CONFIG.piePagina;
  // Solo poner logo si no hay ninguno guardado o está corrupto
  if (!stored.logoURL || stored.logoURL.length < 100) {
    stored.logoURL = DEFAULT_CONFIG.logoURL;
  }
  localStorage.setItem(KEYS.config, JSON.stringify(stored));
  localStorage.setItem(CONFIG_SEED_V_KEY, CONFIG_SEED_V);
}
export function getConfig(): EmpresaConfig {
  try {
    const stored = JSON.parse(localStorage.getItem(KEYS.config) ?? '{}') as Partial<EmpresaConfig>;
    // El logo corporativo es SIEMPRE el oficial — no puede ser modificado por usuarios
    stored.logoURL = DEFAULT_LOGO_URL;
    return { ...DEFAULT_CONFIG, ...stored };
  }
  catch { return DEFAULT_CONFIG; }
}
export function saveConfig(c: Partial<EmpresaConfig>): void {
  // Ignorar cualquier intento de cambiar el logoURL desde el exterior
  const rest = { ...c } as Partial<EmpresaConfig>;
  delete rest.logoURL;
  localStorage.setItem(KEYS.config, JSON.stringify(sanitizeObject({ ...getConfig(), ...rest })));
}

// ── Clientes ──────────────────────────────────────────────────────────────────
export function getClientes(): Cliente[] { return read<Cliente>(KEYS.clientes); }
export function saveClientes(d: Cliente[]): void { write(KEYS.clientes, d); }

export function crearCliente(data: Omit<Cliente, 'id' | 'fechaAlta'>): Cliente {
  const c: Cliente = { ...sanitizeObject(data), id: crypto.randomUUID(), fechaAlta: today() };
  saveClientes([c, ...getClientes()]);
  return c;
}
export function actualizarCliente(c: Cliente): void {
  saveClientes(getClientes().map(x => (x.id === c.id ? sanitizeObject(c) : x)));
}
export function eliminarCliente(id: string): void {
  saveClientes(getClientes().filter(x => x.id !== id));
}

// Seed clientes demo (solo primera vez)
export function ensureClientesSeed(): void {
  if (getClientes().length > 0) return;
  const demos: Omit<Cliente, 'id' | 'fechaAlta'>[] = [
    { nombre: 'Juan García López', nif: '12345678A', direccion: 'Calle Mayor 10, 3ºB', ciudad: 'Madrid', cp: '28001', email: 'jgarcia@email.com', telefono: '600 112 233', notas: '', tipo: 'particular' },
    { nombre: 'Transportes Martínez S.L.', nif: 'B87654321', direccion: 'Polígono Industrial La Fuente, nave 5', ciudad: 'Valencia', cp: '46014', email: 'admin@tmartinez.com', telefono: '961 234 567', notas: 'Cliente preferente', tipo: 'empresa' },
    { nombre: 'AXA Seguros España', nif: 'A28007587', direccion: 'Paseo de la Castellana 79', ciudad: 'Madrid', cp: '28046', email: 'siniestros@axa.es', telefono: '900 202 020', notas: 'Código perito: P-4421', tipo: 'aseguradora' },
  ];
  demos.forEach(d => crearCliente(d));
}

// ── Productos y Servicios ─────────────────────────────────────────────────────
export function getProductos(): ProductoServicio[] { return read<ProductoServicio>(KEYS.productos); }
export function saveProductos(d: ProductoServicio[]): void { write(KEYS.productos, d); }

export function crearProducto(data: Omit<ProductoServicio, 'id'>): ProductoServicio {
  const p: ProductoServicio = { ...sanitizeObject(data), id: crypto.randomUUID() };
  saveProductos([...getProductos(), p]);
  return p;
}
export function actualizarProducto(p: ProductoServicio): void {
  saveProductos(getProductos().map(x => (x.id === p.id ? sanitizeObject(p) : x)));
}
export function eliminarProducto(id: string): void {
  saveProductos(getProductos().filter(x => x.id !== id));
}

// Seed productos demo — sector reformas, albañilería y fontanería
// Versión 2: se fuerza re-seed si el usuario tenía datos del sector anterior
const SEED_VERSION_KEY = 'pp_productos_seed_v';
const SEED_VERSION = '2';

export function ensureProductosSeed(): void {
  const seeded = localStorage.getItem(SEED_VERSION_KEY);
  if (seeded === SEED_VERSION) return; // ya tiene los datos de esta versión

  // Borrar catálogo anterior y reemplazar con el del nuevo sector
  localStorage.removeItem(KEYS.productos);

  const demos: Omit<ProductoServicio, 'id'>[] = [
    // ── Mano de obra ───────────────────────────────────────────────
    { codigo: 'MO-001', nombre: 'Oficial 1ª albañil',         descripcion: 'Hora de oficial de primera en trabajos de albañilería general',                     precioBase: 22,   iva: 21, descuento: 0, unidad: 'h',        categoria: 'Mano de obra',    activo: true },
    { codigo: 'MO-002', nombre: 'Peón de albañilería',        descripcion: 'Hora de peón en tareas de apoyo, acarreo y preparación de materiales',               precioBase: 16,   iva: 21, descuento: 0, unidad: 'h',        categoria: 'Mano de obra',    activo: true },
    { codigo: 'MO-003', nombre: 'Oficial 1ª fontanero',       descripcion: 'Hora de oficial de primera especialista en instalaciones de fontanería y saneamiento', precioBase: 24,   iva: 21, descuento: 0, unidad: 'h',        categoria: 'Mano de obra',    activo: true },
    { codigo: 'MO-004', nombre: 'Oficial 1ª pintor',          descripcion: 'Hora de oficial pintor en trabajos de pintura interior y exterior',                   precioBase: 20,   iva: 21, descuento: 0, unidad: 'h',        categoria: 'Mano de obra',    activo: true },
    { codigo: 'MO-005', nombre: 'Oficial 1ª solador',         descripcion: 'Hora de oficial especialista en solados, alicatados y revestimientos cerámicos',      precioBase: 22,   iva: 21, descuento: 0, unidad: 'h',        categoria: 'Mano de obra',    activo: true },
    // ── Demolición y gestión de residuos ──────────────────────────
    { codigo: 'DEM-001', nombre: 'Demolición tabique',        descripcion: 'Demolición de tabique de ladrillo o pladur, retirada y carga de escombros',           precioBase: 18,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Demolición',      activo: true },
    { codigo: 'DEM-002', nombre: 'Demolición pavimento',      descripcion: 'Levantado de pavimento cerámico o hidráulico existente con medios manuales',          precioBase: 12,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Demolición',      activo: true },
    { codigo: 'DEM-003', nombre: 'Contenedor de escombros',   descripcion: 'Suministro, colocación y retirada de contenedor de escombros de 6 m³',                precioBase: 220,  iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Demolición',      activo: true },
    // ── Albañilería ───────────────────────────────────────────────
    { codigo: 'ALB-001', nombre: 'Tabique ladrillo hueco',    descripcion: 'Tabique de ladrillo hueco doble 7 cm, con mortero de cemento, sin revestir',          precioBase: 28,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Albañilería',     activo: true },
    { codigo: 'ALB-002', nombre: 'Tabique pladur 78 mm',      descripcion: 'Tabique de pladur 78 mm (2×15 mm + estructura 48 mm), con lana de roca',              precioBase: 35,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Albañilería',     activo: true },
    { codigo: 'ALB-003', nombre: 'Enfoscado de mortero',      descripcion: 'Enfoscado maestreado con mortero de cemento en paramentos verticales',                 precioBase: 14,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Albañilería',     activo: true },
    { codigo: 'ALB-004', nombre: 'Guarnecido y enlucido yeso', descripcion: 'Guarnecido maestreado + enlucido de yeso en paredes y techos',                       precioBase: 12,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Albañilería',     activo: true },
    { codigo: 'ALB-005', nombre: 'Rozas para instalaciones',  descripcion: 'Ejecución de roza en pared de fábrica para paso de tuberías o cableado',              precioBase: 8,    iva: 21, descuento: 0, unidad: 'm',        categoria: 'Albañilería',     activo: true },
    // ── Solados y alicatados ──────────────────────────────────────
    { codigo: 'SOL-001', nombre: 'Solado gres porcelánico',   descripcion: 'Suministro y colocación de gres porcelánico 60×60 cm con adhesivo y lechada',         precioBase: 38,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Solados',         activo: true },
    { codigo: 'SOL-002', nombre: 'Alicatado azulejo baño',    descripcion: 'Suministro y colocación de azulejo cerámico 30×60 cm en paramentos de baño/cocina',   precioBase: 35,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Solados',         activo: true },
    { codigo: 'SOL-003', nombre: 'Rodapié cerámica',          descripcion: 'Suministro y colocación de rodapié cerámico, incluso ingletes',                        precioBase: 12,   iva: 21, descuento: 0, unidad: 'm',        categoria: 'Solados',         activo: true },
    // ── Fontanería ────────────────────────────────────────────────
    { codigo: 'FON-001', nombre: 'Tubería multicapa Ø16',     descripcion: 'Suministro y colocación de tubería multicapa Ø16 mm para agua fría/caliente',         precioBase: 9,    iva: 21, descuento: 0, unidad: 'm',        categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-002', nombre: 'Tubería PVC saneamiento Ø110', descripcion: 'Suministro y colocación de tubería PVC evacuación Ø110 mm, con accesorios',        precioBase: 14,   iva: 21, descuento: 0, unidad: 'm',        categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-003', nombre: 'Grifo monomando lavabo',    descripcion: 'Suministro y colocación de grifo monomando de lavabo, gama media',                     precioBase: 95,   iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-004', nombre: 'Grifo monomando ducha',     descripcion: 'Suministro y colocación de grifo termostático de ducha, gama media',                   precioBase: 145,  iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-005', nombre: 'Inodoro completo',          descripcion: 'Suministro y colocación de inodoro con cisterna, tapa y accesorios de fijación',       precioBase: 280,  iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-006', nombre: 'Plato de ducha',            descripcion: 'Suministro y colocación de plato de ducha acrílico 80×80 cm, con desagüe',            precioBase: 230,  iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-007', nombre: 'Calentador eléctrico 80 l', descripcion: 'Suministro y colocación de calentador eléctrico 80 litros, con conexiones',            precioBase: 320,  iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-008', nombre: 'Válvula de corte 1/2"',     descripcion: 'Suministro y colocación de válvula de esfera de paso 1/2", con racores',               precioBase: 18,   iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    { codigo: 'FON-009', nombre: 'Bote sifónico',             descripcion: 'Suministro y colocación de bote sifónico PVC Ø110 con tapa y cuatro entradas',         precioBase: 35,   iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Fontanería',      activo: true },
    // ── Pintura ───────────────────────────────────────────────────
    { codigo: 'PIN-001', nombre: 'Pintura plástica interior', descripcion: 'Pintura plástica mate en paredes interiores, dos manos + imprimación',                  precioBase: 8,    iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Pintura',         activo: true },
    { codigo: 'PIN-002', nombre: 'Pintura techo',             descripcion: 'Pintura plástica blanca para techos, dos manos + mano de imprimación',                  precioBase: 7,    iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Pintura',         activo: true },
    { codigo: 'PIN-003', nombre: 'Pintura exterior fachada',  descripcion: 'Pintura plástica elastomérica para fachada, con imprimación + dos manos',               precioBase: 14,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Pintura',         activo: true },
    { codigo: 'PIN-004', nombre: 'Pintura esmalte carpintería', descripcion: 'Esmalte sintético para puertas, ventanas y carpintería de madera o metálica',         precioBase: 18,   iva: 21, descuento: 0, unidad: 'm²',       categoria: 'Pintura',         activo: true },
    // ── Carpintería ───────────────────────────────────────────────
    { codigo: 'CAR-001', nombre: 'Puerta interior block',     descripcion: 'Suministro y colocación de puerta block de paso con marco y manivela, lisa',           precioBase: 380,  iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Carpintería',     activo: true },
    { codigo: 'CAR-002', nombre: 'Ventana PVC 1 hoja',        descripcion: 'Suministro y colocación de ventana PVC 1 hoja oscilobatiente con doble acristalamiento', precioBase: 290, iva: 21, descuento: 0, unidad: 'ud',       categoria: 'Carpintería',     activo: true },
    // ── Gestión y estudios ────────────────────────────────────────
    { codigo: 'GES-001', nombre: 'Visita técnica / medición', descripcion: 'Desplazamiento, toma de medidas y elaboración de memoria descriptiva',                  precioBase: 80,   iva: 21, descuento: 0, unidad: 'servicio', categoria: 'Gestión',         activo: true },
    { codigo: 'GES-002', nombre: 'Gestión de licencia de obra menor', descripcion: 'Tramitación de licencia de obra menor ante el Ayuntamiento',                   precioBase: 250,  iva: 21, descuento: 0, unidad: 'servicio', categoria: 'Gestión',         activo: true },
  ];
  demos.forEach(d => crearProducto(d));
  localStorage.setItem(SEED_VERSION_KEY, SEED_VERSION);
}

// ── Exportar / Importar datos ─────────────────────────────────────────────────

export interface AppBackup {
  version: number;
  exportedAt: string;
  presupuestos: ReturnType<typeof getPresupuestos>;
  clientes: ReturnType<typeof getClientes>;
  productos: ReturnType<typeof getProductos>;
  config: ReturnType<typeof getConfig>;
  counter: string;
}

export function exportarDatos(): AppBackup {
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    presupuestos: getPresupuestos(),
    clientes: getClientes(),
    productos: getProductos(),
    config: getConfig(),
    counter: localStorage.getItem(KEYS.counter) ?? '0',
  };
}

export function importarDatos(backup: AppBackup): void {
  const check = validateBackupData(backup);
  if (!check.valid) throw new Error(check.error ?? 'Formato de copia de seguridad no válido.');
  write(KEYS.presupuestos, (backup.presupuestos ?? []).map(p => sanitizeObject(p as Presupuesto)));
  write(KEYS.clientes, (backup.clientes ?? []).map(c => sanitizeObject(c as Cliente)));
  write(KEYS.productos, (backup.productos ?? []).map(p => sanitizeObject(p as ProductoServicio)));
  localStorage.setItem(KEYS.config, JSON.stringify(sanitizeObject(backup.config ?? {} as EmpresaConfig)));
  localStorage.setItem(KEYS.counter, String(backup.counter ?? '0'));
}

export function getUltimaExportacion(): string | null {
  return localStorage.getItem('pp_last_export');
}
export function setUltimaExportacion(): void {
  localStorage.setItem('pp_last_export', new Date().toISOString());
}
